import { LayoutModule } from '@angular/cdk/layout';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, FactoryProvider, NgModule } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, RouterOutlet } from '@angular/router';
import { ApiUrlLoader } from '@mzima-client/sdk';
import { MatomoRouteDataInterceptor, NgxMatomoRouterModule } from '@ngx-matomo/router';
import {
  MatomoInitializationMode,
  MatomoInitializerService,
  NgxMatomoTrackerModule,
} from '@ngx-matomo/tracker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { EnvService, SiteConfigService } from '@services';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { CustomTranslateHttpLoader } from './core/services/translate-http-loader';
import { ReportModule } from './pages/report/report.module';
import { FooterModule } from './shared/components/footer/footer.module';
import { NavbarModule } from './shared/components/navbar/navbar.module';
import { SharedModule } from './shared/components/shared.module';

export function HttpLoaderFactory(http: HttpClient): any {
  return new CustomTranslateHttpLoader(http, './assets/locales/', '.json');
}

export function loadConfigFactory(
  envService: EnvService,
  siteConfigService: SiteConfigService,
  matomoInitializer: MatomoInitializerService,
) {
  return () =>
    Promise.all([
      envService.initEnv().then(() => {
        const siteId = envService.environment.matomo_site_id;
        if (siteId) {
          matomoInitializer.initializeTracker({
            siteId: siteId,
            scriptUrl: `${envService.environment.matomo_url}/matomo.js`,
            trackerUrl: envService.environment.matomo_url,
          });
        }
      }),
      siteConfigService.loadConfig().toPromise(),
    ]);
}

export const loadConfigProvider: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory: loadConfigFactory,
  deps: [EnvService, SiteConfigService, MatomoInitializerService],
  multi: true,
};

export function EnvLoaderFactory(env: EnvService): any {
  return new ApiUrlLoader(env);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    FooterModule,
    HttpClientModule,
    LayoutModule,
    MatIconModule,
    NavbarModule,
    NgxMatomoRouterModule.forRoot({
      interceptors: [MatomoRouteDataInterceptor],
    }),
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
    }),
    ReportModule,
    RouterModule.forRoot(appRoutes, { initialNavigation: 'enabledBlocking' }),
    RouterOutlet,
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [loadConfigProvider, CookieService],
  bootstrap: [AppComponent],
})
export class AppModule {}
